import { lineInput } from '@trafiku/contracts';
import { ArrowDown, ArrowUp, GripVertical, Minus, Plus, TriangleAlert, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { IssuesBadge, useIssuesById } from '@/components/issues-badge';
import { HoverCard, type HoverTarget, useHoverCard } from '@/components/map/hover-card';
import { STATION_ICON, STATION_SIGN_SIZE } from '@/components/map/icons';
import { GeoJsonLayer } from '@/components/map/layers';
import { FocusOn, HIGHLIGHT, type LineStop, LineStopsLayer } from '@/components/map/line-stops-layer';
import { boundsOf, MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { ShapeEditor } from '@/components/map/shape-editor';
import { ReadOnlyNotice } from '@/components/read-only-notice';
import { ResourceList } from '@/components/resource-list';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Card, ColorDot, PageHeader, Spinner } from '@/components/ui/layout';
import { useSortable } from '@/components/ui/sortable';
import { useAuth } from '@/lib/auth';
import { safeHex, textOn } from '@/lib/colors';
import { useFormState } from '@/lib/form';
import { t, tn } from '@/lib/i18n';
import { linesCollection, stationsCollection, useNetwork } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import { insertionIndex } from '@/lib/route';
import { checkStops, orientRoute } from '@/lib/stop-checks';
import type { Line } from '@/lib/types';

export function LinesPage() {
  const network = useNetwork();
  const { can } = useAuth();
  const issues = useIssuesById('directions');
  return (
    <ResourceList<Line>
      title={t('Directions')}
      subtitle={t(
        'One direction of a line: its path and its stations in travel order. Colour and sign come from the line.',
      )}
      map={({ hovered, hover }) => (
        <OverviewMap
          data={linesCollection(network.data?.lines)}
          kind="line"
          hovered={hovered}
          onHover={hover}
          basePath="/directions"
        />
      )}
      resource="lines"
      basePath="/directions"
      canCreate={can('admin')}
      newLabel={t('New direction')}
      columns={[
        {
          header: t('Name'),
          cell: (l) => (
            <span className="flex items-center gap-2 font-medium">
              <ColorDot color={l.color} /> {l.name}
            </span>
          ),
        },
        { header: t('Sign'), cell: (l) => l.bus_sign || '—' },
        { header: t('Stations'), cell: (l) => l.ordered_stations.length },
        {
          header: t('From → to'),
          cell: (l) =>
            l.ordered_stations.length
              ? `${l.ordered_stations[0]!.name} → ${l.ordered_stations.at(-1)!.name}`
              : '—',
        },
        { header: t('Issues'), cell: (l) => <IssuesBadge issues={issues.get(l._id)} /> },
      ]}
    />
  );
}

export function LineEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Line>('lines', id);
  const network = useNetwork();
  const save = useSave<Line>('lines', id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const card = useHoverCard();
  const [hovered, setHovered] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ position: [number, number]; seq: number } | null>(null);
  const form = useFormState({
    name: '',
    description: '',
    ordered_stations: [] as string[],
    geometry: null as Line['geometry'] | null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    if (item.data)
      form.setValues({
        name: item.data.name,
        description: item.data.description ?? '',
        ordered_stations: item.data.ordered_stations.map((s) => s._id),
        geometry: item.data.geometry,
      });
  }, [item.data]);

  const readOnly = !can('admin');
  const sortable = useSortable(
    form.values.ordered_stations,
    (next) => form.set('ordered_stations', next),
    !readOnly,
  );
  const stationById = useMemo(() => new Map(network.data?.stations.map((s) => [s._id, s])), [network.data]);
  if ((!isNew && item.isLoading) || !network.data) return <Spinner />;
  const { values, set, errors } = form;
  const ordered = values.ordered_stations;
  const move = (i: number, d: number) => {
    const next = [...ordered];
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    set('ordered_stations', next);
  };
  /** Adds a stop where it lies along the route (or at the end when there is no route yet). */
  const add = (sid: string) => {
    const station = stationById.get(sid);
    if (!station || ordered.includes(sid)) return;
    const at = insertionIndex(
      orientRoute(values.geometry?.coordinates, ordered, stationById),
      ordered,
      (id) => stationById.get(id)?.geometry.coordinates,
      station.geometry.coordinates,
    );
    set('ordered_stations', [...ordered.slice(0, at), sid, ...ordered.slice(at)]);
  };
  const remove = (sid: string) =>
    set(
      'ordered_stations',
      ordered.filter((x) => x !== sid),
    );
  const lineNames = (sid: string) =>
    network.data?.lines
      .filter((l) => l._id !== id && l.ordered_stations.includes(sid))
      .map((l) => l.name)
      .join(', ');
  const stops = checkStops(values.geometry?.coordinates, ordered, stationById);
  const issues = stops.filter((x) => x.far || x.outOfOrder).length;
  const color = safeHex(item.data?.color, '#0b3f82');
  const highlight = hovered ?? card.target?.id ?? null;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(lineInput, (d) => save.mutateAsync(d))) navigate('/directions');
      }}
    >
      <PageHeader
        title={isNew ? t('New direction') : values.name || t('Direction')}
        back="/directions"
        subtitle={t('Draw the route, then list the stations in the order the bus serves them.')}
        actions={
          !isNew && (
            <span className="flex gap-3 text-sm">
              <Link to={`/schedules/directions/${id}`} className="font-medium text-brand-700 hover:underline">
                {t('Timetable')}
              </Link>
              <Link to={`/performance/${id}`} className="font-medium text-brand-700 hover:underline">
                {t('Performance')}
              </Link>
            </span>
          )
        }
      />
      {readOnly && <ReadOnlyNotice />}
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card>
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            <Field label={t('Name')} error={errors.name}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  invalid={!!errors.name}
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('Route')} error={errors.geometry}>
              {() => (
                <p className="text-xs text-slate-600">
                  {values.geometry
                    ? tn(values.geometry.coordinates.length, '{n} point', '{n} points')
                    : t('Draw the route on the map')}
                </p>
              )}
            </Field>
            <Field
              label={t('Stations in order ({n})', { n: ordered.length })}
              error={errors.ordered_stations}
              hint={t(
                'Drag a stop to change the order. Point at a station on the map to add or remove it; new stops are placed by their position on the route.',
              )}
            >
              {() => (
                <div className="space-y-2">
                  <ol className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
                    {stops.map((stop, i) => {
                      const sid = stop.id;
                      const badge =
                        sid === highlight ? HIGHLIGHT : stop.far || stop.outOfOrder ? '#d97706' : color;
                      return (
                        <li
                          key={sid}
                          {...sortable.itemProps(i)}
                          className={`group relative flex items-center gap-2 px-2 py-1.5 text-sm ${sid === highlight ? 'bg-amber-50' : 'bg-white'} ${sortable.dragging === i ? 'opacity-40' : ''}`}
                          onMouseEnter={() => setHovered(sid)}
                          onMouseLeave={() => setHovered(null)}
                        >
                          {sortable.dropLine(i) && (
                            <span
                              className={`pointer-events-none absolute inset-x-0 h-0.5 bg-brand-600 ${sortable.dropLine(i) === 'before' ? '-top-px' : '-bottom-px'}`}
                            />
                          )}
                          {!readOnly && (
                            <GripVertical
                              className="size-4 shrink-0 cursor-grab text-slate-300 group-hover:text-slate-500"
                              aria-hidden
                            />
                          )}
                          <span
                            className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[11px] font-bold"
                            style={{ background: badge, color: textOn(badge) }}
                          >
                            {i + 1}
                          </span>
                          <button
                            type="button"
                            className="min-w-0 flex-1 text-left"
                            title={t('Show on the map')}
                            onClick={() =>
                              stop.position && setFocus({ position: stop.position, seq: Date.now() })
                            }
                          >
                            <span className="block truncate">{stop.name}</span>
                            {(stop.far || stop.outOfOrder) && (
                              <span className="flex items-center gap-1 text-xs text-amber-700">
                                <TriangleAlert className="size-3 shrink-0" />
                                {stop.outOfOrder
                                  ? t('Out of order: the route reaches it earlier')
                                  : t('{m} m from the route', { m: stop.distance })}
                              </span>
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={i === 0}
                            onClick={() => move(i, -1)}
                            className="text-slate-400 hover:text-slate-800 disabled:opacity-30"
                            aria-label={t('Move up')}
                          >
                            <ArrowUp className="size-4" />
                          </button>
                          <button
                            type="button"
                            disabled={i === ordered.length - 1}
                            onClick={() => move(i, 1)}
                            className="text-slate-400 hover:text-slate-800 disabled:opacity-30"
                            aria-label={t('Move down')}
                          >
                            <ArrowDown className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(sid)}
                            className="text-slate-400 hover:text-red-600"
                            aria-label={t('Remove')}
                          >
                            <X className="size-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                  {issues > 0 && (
                    <p className="text-xs text-amber-700">
                      {tn(
                        issues,
                        '{n} stop needs a look: it is away from the route or out of order.',
                        '{n} stops need a look: they are away from the route or out of order.',
                      )}
                    </p>
                  )}
                  <Combobox
                    value=""
                    resetOnSelect
                    onChange={add}
                    placeholder={t('Add a station: type its name…')}
                    options={[...(network.data?.stations ?? [])]
                      .filter((s) => !ordered.includes(s._id))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => ({ value: s._id, label: s.name, hint: lineNames(s._id) || undefined }))}
                  />
                </div>
              )}
            </Field>
          </fieldset>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="lines"
              id={id}
              backTo="/directions"
              canDelete={can('admin')}
              canSave={can('admin')}
              deleteMessage={t(
                'Directions used by a line or by buses cannot be deleted. Timetables of this direction are removed.',
              )}
            />
          </div>
        </Card>
        <MapView className="h-[680px]" fit={boundsOf(values.geometry?.coordinates)}>
          <GeoJsonLayer
            data={stationsCollection(network.data?.stations.filter((s) => !ordered.includes(s._id)))}
            layers={[
              {
                type: 'symbol',
                layout: {
                  'icon-image': STATION_ICON,
                  'icon-size': STATION_SIGN_SIZE,
                  'icon-allow-overlap': true,
                },
                paint: { 'icon-opacity': 0.55 },
              },
            ]}
            onHover={(p) => card.onFeatureHover(p && target(p))}
            onClick={(p) => card.onFeatureClick(target(p))}
          />
          {card.target && stationById.get(card.target.id) && (
            <HoverCard at={card.target.lngLat} events={card.cardEvents}>
              <StationCard
                name={stationById.get(card.target.id)!.name}
                stop={ordered.indexOf(card.target.id)}
                otherLines={lineNames(card.target.id)}
                canEdit={can('admin')}
                onAdd={() => {
                  add(card.target!.id);
                  card.close();
                }}
                onRemove={() => {
                  remove(card.target!.id);
                  card.close();
                }}
              />
            </HoverCard>
          )}
          <ShapeEditor
            readOnly={readOnly}
            slots={[{ key: 'route', label: t('Route'), kind: 'LineString', color, value: values.geometry }]}
            onChange={(_, g) => set('geometry', (g as Line['geometry']) ?? null)}
          />
          {/* After the route editor, so the stops are drawn above the route and its points. */}
          <LineStopsLayer
            stops={stops.filter((x): x is typeof x & LineStop => !!x.position)}
            color={color}
            highlight={highlight}
            onHover={(p) => card.onFeatureHover(p && target(p))}
            onClick={(p) => card.onFeatureClick(target(p))}
          />
          <FocusOn at={focus} />
        </MapView>
      </div>
    </form>
  );
}

const target = (p: Record<string, unknown>): HoverTarget => ({
  id: String(p.id),
  lngLat: [Number(p.lng), Number(p.lat)],
});

function StationCard({
  name,
  stop,
  otherLines,
  canEdit,
  onAdd,
  onRemove,
}: {
  name: string;
  /** Index in the line's stops, -1 when not on it. */
  stop: number;
  otherLines?: string;
  canEdit: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <div className="font-medium text-slate-900">{name}</div>
        <div className="text-xs text-slate-500">
          {stop >= 0 ? t('Stop {n} of this direction', { n: stop + 1 }) : t('Not on this direction')}
        </div>
        {otherLines && (
          <div className="mt-1 text-xs text-slate-500">{t('Also on: {lines}', { lines: otherLines })}</div>
        )}
      </div>
      {canEdit &&
        (stop >= 0 ? (
          <Button size="sm" variant="secondary" icon={<Minus className="size-4" />} onClick={onRemove}>
            {t('Remove from direction')}
          </Button>
        ) : (
          <Button size="sm" icon={<Plus className="size-4" />} onClick={onAdd}>
            {t('Add to direction')}
          </Button>
        ))}
    </div>
  );
}
